import {
  formatRescheduleSlotDisplay,
  isValidIanaTimezone,
  resolveHostSlotDurationValue,
  resolveManualMeetEndTime,
  normalizeTimeValue,
  toIsoInTimezone,
} from '../../HireWithUs/Pages/Applications/meetingScheduleUtils';

export function buildAvailabilityNotesFromSlots(slots, timezone, slotDurationMinutes) {
  const resolvedDuration = resolveHostSlotDurationValue(
    slotDurationMinutes != null ? { slot_duration_minutes: slotDurationMinutes } : null
  );
  return (slots || [])
    .filter((slot) => slot?.date && slot?.startTime)
    .map((slot) => {
      const endTime =
        normalizeTimeValue(slot.endTime) ||
        resolveManualMeetEndTime({
          startTime: slot.startTime,
          durationValue: slot.duration || resolvedDuration,
        });
      return formatRescheduleSlotDisplay(
        { ...slot, endTime },
        timezone
      );
    })
    .join('\n');
}

export function buildSuggestedSlotPayloadsFromSlots(slots, timezone, slotDurationMinutes) {
  const resolvedDuration = resolveHostSlotDurationValue(
    slotDurationMinutes != null ? { slot_duration_minutes: slotDurationMinutes } : null
  );
  const safeTimezone = isValidIanaTimezone(timezone) ? timezone : 'Asia/Kolkata';
  const seen = new Set();

  return (slots || [])
    .filter((slot) => slot?.date && slot?.startTime)
    .map((slot) => {
      const endTime =
        normalizeTimeValue(slot.endTime) ||
        resolveManualMeetEndTime({
          startTime: slot.startTime,
          durationValue: slot.duration || resolvedDuration,
        });
      const slotValue = toIsoInTimezone(slot.date, slot.startTime, safeTimezone);
      const endSlotValue = endTime ? toIsoInTimezone(slot.date, endTime, safeTimezone) : '';
      return {
        label: formatRescheduleSlotDisplay(
          { ...slot, endTime },
          safeTimezone
        ),
        date: String(slot.date || '').trim(),
        start_time: normalizeTimeValue(slot.startTime),
        end_time: normalizeTimeValue(endTime),
        timezone: safeTimezone,
        original_timezone: safeTimezone,
        slot_value: slotValue || undefined,
        end_slot_value: endSlotValue || undefined,
      };
    })
    .filter((slot) => {
      const key =
        String(slot.slot_value || '').trim() ||
        `${slot.date}|${slot.start_time}|${slot.end_time}|${slot.timezone}`;
      if (!slot.date || !slot.start_time || !slot.end_time || !key) {
        return false;
      }
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
}

export function resolveBookingActionMessage(result, actor) {
  const message = String(result?.message || '').trim();
  if (message) return message;
  const action = String(result?.action || '').trim().toLowerCase();
  if (action === 'accept') {
    return result?.action_status === 'confirmed'
      ? 'Your interview availability has been confirmed. Final meeting details have been sent by email.'
      : 'Your interview availability has been confirmed.';
  }
  if (action === 'reschedule') {
    return 'Your reschedule request has been sent to the host.';
  }
  if (action === 'decline') {
    return 'Your decline has been recorded and everyone involved has been notified.';
  }
  if (action === 'next_slot') {
    return 'Next slot options were shared with the candidate by email.';
  }
  return 'Interview updated successfully.';
}

export const BOOKING_ACTION_LABELS = {
  accept: 'Confirm Interview',
  decline: 'Decline Interview',
  reschedule: 'Request Reschedule',
  next_slot: 'Share Next Slot Options',
};

export function resolveCompletedBookingAction(context) {
  const explicit = String(context?.completed_action || '').trim().toLowerCase();
  if (explicit) return explicit;

  const actor = String(context?.actor || 'candidate').trim().toLowerCase();
  const bookingStatus = String(context?.booking?.status || context?.status || '')
    .trim()
    .toLowerCase();

  if (actor === 'candidate') {
    if (bookingStatus === 'declined_by_candidate') {
      return 'decline';
    }
    if (bookingStatus === 'accepted_by_candidate' || bookingStatus === 'confirmed') {
      return 'accept';
    }
    if (bookingStatus === 'reschedule_requested_by_candidate') {
      return 'reschedule';
    }
  } else if (actor === 'interviewer') {
    if (bookingStatus === 'declined_by_interviewer') {
      return 'decline';
    }
    if (bookingStatus === 'accepted_by_interviewer' || bookingStatus === 'confirmed') {
      return 'accept';
    }
    if (bookingStatus === 'reschedule_requested_by_interviewer') {
      return 'reschedule';
    }
    if (bookingStatus === 'next_slots_shared_by_interviewer') {
      return 'next_slot';
    }
  }

  return '';
}

export function resolveAlreadyUsedActionMessage(context, requestedAction, actor = 'candidate') {
  const completedAction = resolveCompletedBookingAction(context);
  const requested = String(requestedAction || '').trim().toLowerCase();
  const completedLabel =
    BOOKING_ACTION_LABELS[completedAction] || completedAction || 'This action';
  const requestedLabel =
    BOOKING_ACTION_LABELS[requested] || requested || 'This action';

  if (completedAction && requested && completedAction !== requested) {
    return `You already completed "${completedLabel}" from this email link. "${requestedLabel}" is no longer available. Ask your recruiter for a fresh scheduling email if you need another change.`;
  }

  if (completedAction === 'accept') {
    return 'You have already confirmed this interview from this link.';
  }
  if (completedAction === 'decline') {
    return 'You have already declined this interview from this link.';
  }
  if (completedAction === 'reschedule') {
    return 'Your reschedule request has already been sent to the host.';
  }
  if (completedAction === 'next_slot') {
    return 'Next slot options were already shared from this link.';
  }

  return 'This interview link has already been used.';
}
